import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

import MainCard from 'components/MainCard';
import API_BASE_URL from 'config/api';
import useGet from 'hooks/useGet';


const productRowsPerPageOptions = [10, 20, 30, 40, 50];

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const resolveProductImageUrl = (row) => {
  const raw =
    row?.image ||
    row?.imageUrl ||
    row?.thumbnail ||
    row?.thumbnailUrl ||
    row?.featuredImage ||
    row?.coverImage ||
    row?.heroImage ||
    row?.productImage ||
    row?.media?.heroImage ||
    row?.media?.galleryImages?.[0] ||
    row?.galleryImages?.[0] ||
    row?.images?.[0]?.url ||
    row?.images?.[0]?.path ||
    row?.images?.[0];

  if (!raw || typeof raw !== 'string') return '';

  const value = raw.trim().replace(/\\/g, '/');
  if (!value) return '';
  if (/^(https?:|blob:|data:)/i.test(value)) return value;

  const normalized = value.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (normalized.startsWith('media/')) return `${API_BASE_URL}/uploads/${normalized}`;
  if (value.startsWith('/')) return `${API_BASE_URL}${value}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const getComparableValue = (row, key) => {
  const value = row?.[key];
  if (typeof value === 'number') return value;
  return String(value || '').toLowerCase();
};

const sortRows = (rows, orderBy, order) =>
  [...rows].sort((firstRow, secondRow) => {
    const firstValue = getComparableValue(firstRow, orderBy);
    const secondValue = getComparableValue(secondRow, orderBy);

    if (secondValue < firstValue) return order === 'desc' ? -1 : 1;
    if (secondValue > firstValue) return order === 'desc' ? 1 : -1;
    return 0;
  });

const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.products || payload?.data || payload?.items || [];
};

const createProductLookup = (products) => {
  const lookup = new Map();

  getCollection(products).forEach((product) => {
    [
      product?._id,
      product?.id,
      product?.productId,
      product?.sku,
      product?.SKU,
      product?.name,
      product?.title,
      product?.productName
    ].forEach((key) => {
      if (key) lookup.set(String(key).toLowerCase(), product);
    });
  });

  return lookup;
};

const findCatalogProduct = (lookup, item) =>
  [
    item?.productId,
    item?._id,
    item?.id,
    item?.sku,
    item?.SKU,
    item?.name,
    item?.title,
    item?.productName
  ].reduce((match, key) => match || (key ? lookup.get(String(key).toLowerCase()) : null), null);

const normalizeProductAnalyticsRow = (item, catalogProduct = {}) => {
  const orders = Number(item?.orders || 0);
  const views = Number(item?.views || 0);
  const cartAdds = Number(item?.cartAdds || 0);
  const wishlists = Number(item?.wishlists || 0);

  return {
    ...catalogProduct,
    ...item,
    productId: item?.productId || item?._id || item?.id || catalogProduct?._id || catalogProduct?.id || '',
    sku: item?.sku || item?.SKU || catalogProduct?.sku || catalogProduct?.SKU || '',
    name:
      item?.name ||
      item?.title ||
      item?.productName ||
      catalogProduct?.name ||
      catalogProduct?.title ||
      catalogProduct?.productName ||
      'Unnamed product',
    views,
    cartAdds,
    wishlists,
    orders,
    total: Number(item?.total ?? orders + views + cartAdds + wishlists)
  };
};

function ProductAnalyticsRow({ row }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = resolveProductImageUrl(row);
  const showImage = imageUrl && !imageFailed;
  const metrics = [
    { label: 'Views', value: row.views },
    { label: 'Wishlist', value: row.wishlists },
    { label: 'Cart', value: row.cartAdds },
    { label: 'Orders', value: row.orders }
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)'
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 72, sm: 88 },
            height: { xs: 72, sm: 88 },
            flex: '0 0 auto',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#f6f2ed',
            border: '1px solid',
            borderColor: 'divider',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          {showImage ? (
            <Box
              component="img"
              src={imageUrl}
              alt={row.name || 'Product image'}
              onError={() => setImageFailed(true)}
              sx={{ width: 1, height: 1, objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Typography variant="h4" color="text.secondary">
              {(row.name || 'P').charAt(0).toUpperCase()}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {row.name || 'Unnamed product'}
          </Typography>
          <Chip
            size="small"
            label={`SKU: ${row.sku || '-'}`}
            sx={{
              mt: 1,
              height: 28,
              borderRadius: 999,
              bgcolor: '#dcfce7',
              color: '#047857',
              fontWeight: 600,
              '& .MuiChip-label': { px: 1.25 }
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(72px, 1fr))' },
          gap: { xs: 1.5, md: 3.5 },
          minWidth: { md: 380 }
        }}
      >
        {metrics.map((metric) => (
          <Box key={metric.label} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
              {formatNumber(metric.value)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {metric.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ProductAnalyticsTable({ rows, search, onSearchChange }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('total');
  const [order, setOrder] = useState('desc');

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [row.name, row.sku]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => sortRows(filteredRows, orderBy, order), [filteredRows, order, orderBy]);
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
    setPage(0);
  };

  return (
    <MainCard
      title="Product Analytics"
      subheader="Combined product intent data from views, carts, and wishlists."
      content={false}
    >
      <Stack spacing={2} sx={{ p: { xs: 1.25, sm: 2 }, pb: 0 }}>
        <TextField
          size="small"
          label="Filter products"
          placeholder="Search product or SKU"
          fullWidth
          value={search}
          onChange={(event) => {
            onSearchChange(event.target.value);
            setPage(0);
          }}
        />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {[
            { field: 'total', label: 'Total' },
            { field: 'views', label: 'Views' },
            { field: 'wishlists', label: 'Wishlist' },
            { field: 'cartAdds', label: 'Cart' },
            { field: 'orders', label: 'Orders' },
            { field: 'name', label: 'Product' }
          ].map((option) => {
            const active = orderBy === option.field;

            return (
              <Button
                key={option.field}
                size="small"
                variant={active ? 'contained' : 'outlined'}
                color={active ? 'primary' : 'secondary'}
                onClick={() => handleSort(option.field)}
                sx={{ borderRadius: 999, textTransform: 'none', minWidth: 0 }}
              >
                {option.label}
                {active ? ` ${order === 'asc' ? 'Asc' : 'Desc'}` : ''}
              </Button>
            );
          })}
        </Stack>
      </Stack>

      <Stack spacing={1.5} sx={{ p: { xs: 1.25, sm: 2 } }}>
        {visibleRows.length ? (
          visibleRows.map((row) => <ProductAnalyticsRow key={row.productId || row.sku || row.name} row={row} />)
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No product analytics found.</Typography>
          </Box>
        )}
      </Stack>

      <TablePagination
        component="div"
        count={sortedRows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={productRowsPerPageOptions}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        sx={{
          '& .MuiTablePagination-toolbar': {
            px: { xs: 1, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: 0.5
          },
          '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 }
        }}
      />
    </MainCard>
  );
}

// Journey table removed per request

export default function EventsAnalytics() {
  const [refreshing, setRefreshing] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const {
    data: productResponse,
    loading: productsLoading,
    error: productsError,
    mutate: mutateProducts
  } = useGet('/api/events/analytics/products?limit=all');
  const { data: productCatalogResponse, loading: productCatalogLoading } = useGet('/api/products/?limit=all');

  const productAnalytics = useMemo(() => productResponse?.data || {}, [productResponse]);
  const productLookup = useMemo(() => createProductLookup(productCatalogResponse), [productCatalogResponse]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await mutateProducts();
    } finally {
      setRefreshing(false);
    }
  };

  const productRows = useMemo(() => {
    if (Array.isArray(productAnalytics.products)) {
      return productAnalytics.products.map((item) => normalizeProductAnalyticsRow(item, findCatalogProduct(productLookup, item)));
    }

    const products = new Map();

    const ensureProduct = (item) => {
      const key = item?.productId || item?.sku || item?.name || 'unknown-product';
      if (!products.has(key)) {
        const catalogProduct = findCatalogProduct(productLookup, item);
        products.set(key, {
          ...catalogProduct,
          productId: item?.productId || '',
          sku: item?.sku || catalogProduct?.sku || catalogProduct?.SKU || '',
          name: item?.name || catalogProduct?.name || catalogProduct?.title || catalogProduct?.productName || 'Unnamed product',
          views: 0,
          cartAdds: 0,
          wishlists: 0,
          orders: 0,
          total: 0
        });
      }
      return products.get(key);
    };

    const addMetric = (items, field) => {
      (Array.isArray(items) ? items : []).forEach((item) => {
        const product = ensureProduct(item);
        product[field] = Number(item?.count || 0);
        product.total = product.orders + product.views + product.cartAdds + product.wishlists;
      });
    };

    addMetric(productAnalytics.mostViewed, 'views');
    addMetric(productAnalytics.mostAddedToCart, 'cartAdds');
    addMetric(productAnalytics.mostWishlisted, 'wishlists');
    addMetric(productAnalytics.mostOrdered, 'orders');

    return Array.from(products.values());
  }, [productAnalytics, productLookup]);

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box>
          <Typography variant="h4">Customer Analytics</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={refreshAll}
          disabled={refreshing}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'center' } }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh data'}
        </Button>
      </Stack>

      {(productsLoading || productCatalogLoading) && !refreshing ? <LinearProgress /> : null}
      {productsError ? <Alert severity="error">Failed to load product analytics.</Alert> : null}

      <ProductAnalyticsTable rows={productRows} search={productSearch} onSearchChange={setProductSearch} />
    </Stack>
  );
}
